/**
 * Responsible for fetching and caching UnitsOfMeasure
 * @module
 */
import * as _ from 'lodash'
import { get } from 'tools/request'
import { indexUnits, unitAlias } from 'util/quantifier'
import { UnitOfMeasure } from 'types/dtos'
import { unmarshallUnits } from 'util/units'
import { UOMByName } from 'types/UnitOfMeasure'

const UNIT_WHITELIST = ['mmHg', 'units']

const NON_DOSAGE_UNIT_TYPES = ['Length', 'Area']
const TIME_UNIT_TYPES = ['Time']
const BLACKLIST_DOSAGE_UNITS = ['NA', 'patients']

export const validDosageUnit = u => !BLACKLIST_DOSAGE_UNITS.includes(u.Name?.Name) && !NON_DOSAGE_UNIT_TYPES.includes(u.Type?.Name)
export const validTimeUnit = u => TIME_UNIT_TYPES.includes(u.Type?.Name)

let allUoms
let idxUoms
let refdUoms
let idxRefdUoms

export function fetchUnits() {
	if (idxUoms) return Promise.resolve(idxUoms)
	return idxUoms = Promise.all([
		get('/portal/unit'),
		get('/api/unitconversionrules/search?Select=SourceUnit,DestinationUnit')
	]).then(([unitResponse, crules]) => {
		allUoms = unmarshallUnits(unitResponse)
		const refd = new Set()
		const strings = new Set(UNIT_WHITELIST.map(s => s.toUpperCase()))
		crules.forEach(rule => {
			refd.add(rule.SourceUnit.Id)
			refd.add(rule.DestinationUnit.Id)
			rule.SourceUnit.Name.split('/').forEach(s => strings.add(s.toUpperCase()))
			rule.DestinationUnit.Name.split('/').forEach(s => strings.add(s.toUpperCase()))
		})

		refdUoms = allUoms.filter(uom => refd.has(uom.Id) || strings.has(uom.Name.Name.toUpperCase()))
		return idxUoms = indexUnits(allUoms)
	})
}

export function fetchAliases() {
	return get('/portal/UnitAlias').then((mappings: any[]) =>
	  _.transform(mappings, (m, entry) => m[entry.id] = entry.label, {})
	)
}

export function units(): UnitOfMeasure[] {
	return allUoms
}

export function indexedUnits(): UOMByName {
	return idxUoms
}

export function refdUnits(): UnitOfMeasure[] {
	return refdUoms
}

export function indexedRefdUnits(): UOMByName {
	return idxRefdUoms || (idxRefdUoms = indexUnits(refdUoms))
}

export const toUnitsList = units =>
	units.map(unit => ({
		aliases: _.compact([unit.Name?.Name, unitAlias(unit.Name?.Name)]),
		unit
	})).filter(({aliases}) => aliases.length)

export const refdUnitList = _.once(() => toUnitsList(refdUnits()))

const dosageUnits = _.once(() => units().filter(uom => validDosageUnit(uom)))
export const dosageUnitIndex = _.once(() => indexUnits(dosageUnits()))
export const dosageUnitList = _.once(() => toUnitsList(dosageUnits()))

const timepointUnits = _.once(() => units().filter(uom => validTimeUnit(uom)))
export const timepointUnitIndex = _.once(() => indexUnits(timepointUnits()))
export const timepointUnitList = _.once(() => toUnitsList(timepointUnits()))
